// Product data types and utilities
import { v4 as uuidv4 } from 'uuid';
import { Item } from '../types';

// API endpoint for product data
const PRODUCT_API_URL = 'https://script.google.com/macros/s/AKfycbz0J59sebjf7UodCU2gxETl5DqwvECxyW7dDk5ZPxM51HgP93tm20umrgWLUQpVmNER/exec';
const API_KEY = 'mysecret123';
const SHEET_NAME = 'product_variants';

// Local storage key for product data
const PRODUCT_DATA_KEY = 'cps_product_data';
const PRODUCT_DATA_TIMESTAMP_KEY = 'cps_product_data_timestamp';

// Product interface matching the API response
export interface ProductData {
  product: string;
  title: string;
  stock: number;
  price: number;
  discounted_price: number;
  sales_qty: string;
  image: string;
  weight: string;
  volume: number | string;
  size: string;
  flavor: string;
  color: string;
  extra_description: string;
  Category: number;
}

/**
 * Fetch product data from the API
 * @returns Promise<ProductData[]> Array of product data
 */
export const fetchProductData = async (): Promise<ProductData[]> => {
  try {
    const url = `${PRODUCT_API_URL}?key=${API_KEY}&sheet=${SHEET_NAME}`;
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch product data: ${response.status} ${response.statusText}`);
    }
    
    const data: ProductData[] = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching product data:', error);
    throw error;
  }
};

/**
 * Save product data to local storage
 * @param data ProductData[] Array of product data
 */
export const saveProductData = (data: ProductData[]): void => {
  try {
    localStorage.setItem(PRODUCT_DATA_KEY, JSON.stringify(data));
    localStorage.setItem(PRODUCT_DATA_TIMESTAMP_KEY, Date.now().toString());
  } catch (error) {
    console.error('Error saving product data to local storage:', error);
  }
};

/**
 * Load product data from local storage
 * @returns ProductData[] | null Array of product data or null if not found
 */
export const loadProductData = (): ProductData[] | null => {
  try {
    const data = localStorage.getItem(PRODUCT_DATA_KEY);
    if (!data) return null;
    
    return JSON.parse(data) as ProductData[];
  } catch (error) {
    console.error('Error loading product data from local storage:', error);
    return null;
  }
};

/**
 * Get the timestamp of when product data was last updated
 * @returns number | null Timestamp or null if not found
 */
export const getProductDataTimestamp = (): number | null => {
  try {
    const timestamp = localStorage.getItem(PRODUCT_DATA_TIMESTAMP_KEY);
    if (!timestamp) return null;
    
    return parseInt(timestamp, 10);
  } catch (error) {
    console.error('Error getting product data timestamp:', error);
    return null;
  }
};

/**
 * Check if product data needs to be refreshed (older than 24 hours)
 * @returns boolean True if data needs refresh
 */
export const needsProductDataRefresh = (): boolean => {
  const timestamp = getProductDataTimestamp();
  if (!timestamp) return true;
  
  const now = Date.now();
  const oneDay = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
  
  return now - timestamp > oneDay;
};

/**
 * Convert a ProductData object to an Item for the receipt
 * @param product ProductData Product data object
 * @returns Item Item object for receipt
 */
export const productToItem = (product: ProductData): Item => {
  return {
    id: uuidv4(),
    name: product.title,
    price: product.discounted_price || product.price,
    quantity: 1
  };
};

/**
 * Search products by name or title
 * @param query string Search query
 * @returns ProductData[] Matching products
 */
export const searchProducts = (query: string): ProductData[] => {
  const products = loadProductData();
  if (!products || !query) return [];
  
  // Split query into individual words for more flexible matching
  const queryWords = query.toLowerCase().split(/\s+/).filter(word => word.length > 0);
  
  if (queryWords.length === 0) return [];
  
  return products.filter(product => {
    const productNameLower = product.product.toLowerCase();
    const titleLower = product.title.toLowerCase();
    
    // Check if all query words appear in either product name or title
    return queryWords.every(word => 
      productNameLower.includes(word) || titleLower.includes(word)
    );
  });
};
